import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { X, Plug, Loader2, Plus, Trash2 } from "lucide-react";

import api from "../../services/api";
import toastError from "../../errors/toastError";

// Modal de configuración de una "API que la IA puede consultar". El admin fija
// endpoint + token + parámetros; el modelo solo rellena los valores de los
// parámetros DINÁMICOS. El token nunca vuelve del backend (llega `hasToken`):
// al editar se deja vacío = "no cambiar"; centinela "__CLEAR__" para borrarlo.
//
// UI unificada: una sola tabla de parámetros donde cada fila es Fijo (valor que
// se envía siempre) o Dinámico (lo rellena la IA). Al guardar se reparten en los
// dos campos del backend: staticQuery (fijos) y paramKeys (dinámicos).

const emptyForm = {
  name: "",
  description: "",
  method: "GET",
  url: "",
  authHeaderName: "Authorization",
  authToken: "",
  timeoutMs: 8000,
  isActive: true,
};

const newParam = () => ({
  key: "",
  mode: "dinamico", // "fijo" | "dinamico"
  value: "",
  description: "",
  required: false,
});

const BotApiToolModal = ({ open, onClose, tool, whatsappId, onSave }) => {
  const [form, setForm] = useState(emptyForm);
  const [params, setParams] = useState([]);
  const [responseFields, setResponseFields] = useState("");
  const [clearToken, setClearToken] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (tool) {
      setForm({
        name: tool.name || "",
        description: tool.description || "",
        method: tool.method || "GET",
        url: tool.url || "",
        authHeaderName: tool.authHeaderName || "Authorization",
        authToken: "",
        timeoutMs: tool.timeoutMs || 8000,
        isActive: tool.isActive !== false,
      });
      // Se reconstruye la tabla unificada desde los dos campos del backend.
      const dynamicRows = Array.isArray(tool.paramKeys)
        ? tool.paramKeys.map(p => ({
            key: p.key || "",
            mode: "dinamico",
            value: "",
            description: p.description || "",
            required: !!p.required,
          }))
        : [];
      const fixedRows =
        tool.staticQuery && typeof tool.staticQuery === "object"
          ? Object.entries(tool.staticQuery).map(([key, value]) => ({
              key,
              mode: "fijo",
              value: String(value),
              description: "",
              required: false,
            }))
          : [];
      setParams([...dynamicRows, ...fixedRows]);
      setResponseFields(Array.isArray(tool.responseFields) ? tool.responseFields.join(", ") : "");
    } else {
      setForm(emptyForm);
      setParams([]);
      setResponseFields("");
    }
    setClearToken(false);
  }, [tool, open]);

  const set = (key, value) => setForm(prev => ({ ...prev, [key]: value }));
  const setParam = (i, patch) =>
    setParams(prev => prev.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  const removeParam = i => setParams(prev => prev.filter((_, j) => j !== i));

  const handleSave = async () => {
    if (!form.name.trim()) { toast.warn("Ingresa un nombre para la API"); return; }
    if (!form.description.trim()) { toast.warn("Describe qué hace y cuándo usarla"); return; }
    if (!form.url.trim()) { toast.warn("Ingresa la URL del endpoint"); return; }

    // Se reparten las filas en los dos campos del backend.
    const paramKeys = params
      .filter(p => p.mode === "dinamico" && p.key.trim())
      .map(p => ({
        key: p.key.trim(),
        description: p.description.trim() || undefined,
        required: !!p.required,
      }));
    const staticQuery = params
      .filter(p => p.mode === "fijo" && p.key.trim())
      .reduce((acc, p) => { acc[p.key.trim()] = p.value; return acc; }, {});

    const payload = {
      name: form.name,
      description: form.description,
      method: form.method,
      url: form.url,
      authHeaderName: form.authHeaderName || "Authorization",
      timeoutMs: Number(form.timeoutMs) || 8000,
      isActive: form.isActive,
      whatsappId: whatsappId || null,
      paramKeys,
      staticQuery,
      responseFields: responseFields
        .split(/[,\n]/)
        .map(s => s.trim())
        .filter(Boolean),
    };

    if (clearToken) payload.authToken = "__CLEAR__";
    else if (form.authToken.trim()) payload.authToken = form.authToken.trim();

    setSaving(true);
    try {
      if (tool) {
        await api.put(`/bot/api-tools/${tool.id}`, payload);
        toast.success("API actualizada");
      } else {
        await api.post("/bot/api-tools", payload);
        toast.success("API agregada");
      }
      if (onSave) onSave();
      onClose();
    } catch (err) { toastError(err); }
    setSaving(false);
  };

  if (!open) return null;

  // Base sin ancho: para las filas flex se compone con flex-1/min-w-0 o anchos
  // fijos. `inputCls` añade w-full para los campos que ocupan toda la línea.
  const inputBase =
    "px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent";
  const inputCls = `w-full ${inputBase}`;
  const labelCls =
    "block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700 sticky top-0 bg-white dark:bg-slate-900 z-10">
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <Plug className="w-4 h-4 text-indigo-600" />
            {tool ? "Editar API" : "Agregar API"}
          </h2>
          <button onClick={onClose} className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4 bg-slate-50 dark:bg-slate-800/30">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className={labelCls}>Nombre</label>
              <input type="text" value={form.name} onChange={e => set("name", e.target.value)} autoFocus placeholder="consultar_medicos" className={inputCls} />
              <p className="text-[11px] text-slate-400 mt-1">Se normaliza a minúsculas y guiones bajos.</p>
            </div>
            <div>
              <label className={labelCls}>Método</label>
              <select value={form.method} onChange={e => set("method", e.target.value)} className={inputCls}>
                <option value="GET">GET</option>
                <option value="POST">POST</option>
              </select>
            </div>
          </div>

          <div>
            <label className={labelCls}>Descripción (qué hace y cuándo usarla)</label>
            <textarea value={form.description} onChange={e => set("description", e.target.value)} rows={2}
              placeholder="Consulta la lista de médicos. Úsala cuando el cliente pregunte por un médico o una especialidad."
              className={`${inputCls} resize-none`} />
          </div>

          <div>
            <label className={labelCls}>URL del endpoint</label>
            <input type="text" value={form.url} onChange={e => set("url", e.target.value)}
              placeholder="https://web-api.cmsiglo21.app/api/v1/especialistas" className={`${inputCls} font-mono`} />
            <p className="text-[11px] text-slate-400 mt-1">
              Usa <code>{"{clave}"}</code> para insertar un parámetro en la ruta. Los parámetros no usados en la ruta van como query (GET) o body (POST).
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Cabecera de auth</label>
              <input type="text" value={form.authHeaderName} onChange={e => set("authHeaderName", e.target.value)} placeholder="Authorization" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Token / valor de la cabecera</label>
              <input
                type="password"
                value={form.authToken}
                onChange={e => { set("authToken", e.target.value); setClearToken(false); }}
                placeholder={tool?.hasToken ? "•••••••• (sin cambios)" : "Bearer xxxxx  ·  o una API key"}
                className={inputCls}
              />
              {tool?.hasToken && (
                <label className="flex items-center gap-1.5 mt-1 text-[11px] text-slate-500">
                  <input type="checkbox" checked={clearToken} onChange={e => setClearToken(e.target.checked)} />
                  Quitar el token guardado
                </label>
              )}
            </div>
          </div>

          {/* Parámetros unificados: cada fila es Fijo o Dinámico */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className={`${labelCls} mb-0`}>Parámetros</label>
              <button type="button" onClick={() => setParams(prev => [...prev, newParam()])}
                className="flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-700">
                <Plus className="w-3.5 h-3.5" /> Añadir
              </button>
            </div>
            <p className="text-[11px] text-slate-400 mb-2">
              <b>Fijo</b>: el valor se envía siempre igual (ej. <code>publicado=true</code>). <b>Dinámico</b>: lo rellena la IA con lo que pida el cliente (ej. el nombre del médico).
            </p>

            {params.length === 0 ? (
              <p className="text-[11px] text-slate-400">Sin parámetros: la IA llamará al endpoint tal cual.</p>
            ) : (
              <div className="space-y-2">
                {params.map((p, i) => (
                  <div key={i} className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-2.5 space-y-2">
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={p.key}
                        onChange={e => setParam(i, { key: e.target.value })}
                        placeholder="clave (ej. filtro)"
                        className={`${inputBase} font-mono flex-1 min-w-0`}
                      />
                      <select
                        value={p.mode}
                        onChange={e => setParam(i, { mode: e.target.value })}
                        className={`${inputBase} w-40 shrink-0 ${p.mode === "fijo" ? "text-slate-700 dark:text-slate-300" : "text-indigo-600 dark:text-indigo-400 font-medium"}`}
                      >
                        <option value="dinamico">Dinámico (IA)</option>
                        <option value="fijo">Fijo</option>
                      </select>
                      <button type="button" onClick={() => removeParam(i)}
                        title="Quitar parámetro"
                        className="p-2 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 shrink-0">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>

                    {p.mode === "fijo" ? (
                      <input
                        type="text"
                        value={p.value}
                        onChange={e => setParam(i, { value: e.target.value })}
                        placeholder="valor fijo (ej. true, 20)"
                        className={inputCls}
                      />
                    ) : (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={p.description}
                          onChange={e => setParam(i, { description: e.target.value })}
                          placeholder="descripción para la IA (ej. nombre o apellido del médico)"
                          className={`${inputBase} flex-1 min-w-0`}
                        />
                        <label className="flex items-center gap-1.5 text-[11px] text-slate-500 whitespace-nowrap shrink-0 cursor-pointer">
                          <input type="checkbox" checked={p.required} onChange={e => setParam(i, { required: e.target.checked })} />
                          obligatorio
                        </label>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className={labelCls}>Campos de respuesta (opcional)</label>
            <input
              type="text"
              value={responseFields}
              onChange={e => setResponseFields(e.target.value)}
              placeholder="nombre, apellidos, especialidad.nombre, ubicacion, contactos"
              className={`${inputCls} font-mono`}
            />
            <p className="text-[11px] text-slate-400 mt-1">
              Deja solo estos campos de la respuesta para la IA (separa por comas; usa <code>a.b</code> para anidados). Evita que respuestas grandes se corten. Vacío = respuesta completa.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3 items-end">
            <div>
              <label className={labelCls}>Timeout (ms)</label>
              <input type="number" value={form.timeoutMs} onChange={e => set("timeoutMs", e.target.value)} min={1000} max={30000} className={inputCls} />
            </div>
            <label className="flex items-center justify-between px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
              <span className="text-sm text-slate-700 dark:text-slate-300">Activa</span>
              <button type="button" onClick={() => set("isActive", !form.isActive)}
                className={`relative w-11 h-6 rounded-full transition-colors ${form.isActive ? "bg-indigo-600" : "bg-slate-300 dark:bg-slate-600"}`}>
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form.isActive ? "translate-x-5" : ""}`} />
              </button>
            </label>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200 dark:border-slate-700 sticky bottom-0 bg-white dark:bg-slate-900">
          <button type="button" onClick={onClose} disabled={saving} className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
            Cancelar
          </button>
          <button onClick={handleSave} disabled={saving} className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors">
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {tool ? "Guardar" : "Agregar"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BotApiToolModal;
